import { useCallback, useEffect, useState } from 'react'

// Sort keys served by GET /api/customers and GET /api/vehicles. The
// worker validates the incoming value and falls back to `next_activity`
// if anything else slips through, so this client-side type is a
// convenience for autocomplete + the SortDropdown options array — not
// a security boundary.
export const LIST_SORTS = [
  'next_activity',
  'alpha',
  'recent',
  'last_activity',
] as const
export type ListSort = (typeof LIST_SORTS)[number]
export type ListSortDir = 'asc' | 'desc'
export const DEFAULT_LIST_SORT: ListSort = 'next_activity'

// Natural default direction per sort. Picking "soonest first" for
// upcoming activity, "newest first" for the two recency sorts, and
// A→Z for alphabetical. The direction toggle button flips from this
// baseline.
const DEFAULT_DIR: Record<ListSort, ListSortDir> = {
  next_activity: 'asc',
  alpha: 'asc',
  recent: 'desc',
  last_activity: 'desc',
}

export function defaultDirFor(sort: ListSort): ListSortDir {
  return DEFAULT_DIR[sort]
}

type ListSortEntity = 'customers' | 'vehicles'

type StoredSort = { sort: ListSort; dir: ListSortDir }

function storageKey(entity: ListSortEntity): string {
  return `autoflo.list_sort.${entity}`
}

function readInitial(entity: ListSortEntity): StoredSort {
  const fallback: StoredSort = {
    sort: DEFAULT_LIST_SORT,
    dir: DEFAULT_DIR[DEFAULT_LIST_SORT],
  }
  if (typeof window === 'undefined') return fallback
  try {
    const raw = window.localStorage.getItem(storageKey(entity))
    if (!raw) return fallback
    // Back-compat: previous v1 stored the sort key as a bare string.
    // Honor that — direction falls back to the default for that sort.
    if ((LIST_SORTS as readonly string[]).includes(raw)) {
      return { sort: raw as ListSort, dir: DEFAULT_DIR[raw as ListSort] }
    }
    const parsed = JSON.parse(raw) as Partial<StoredSort>
    const sort = (LIST_SORTS as readonly string[]).includes(parsed.sort ?? '')
      ? (parsed.sort as ListSort)
      : DEFAULT_LIST_SORT
    const dir = parsed.dir === 'asc' || parsed.dir === 'desc' ? parsed.dir : DEFAULT_DIR[sort]
    return { sort, dir }
  } catch {
    // localStorage may be disabled (Safari Private Browsing pre-15,
    // some embedded contexts) — fall through to the default. The sort
    // still works, it just won't persist across page loads.
    return fallback
  }
  return fallback
}

// Per-user, per-entity sort preference backed by localStorage.
// Returns `{ sort, dir, setSort, toggleDir }`. v1 stores on the
// device — see "Open question Q2" in the Notion ticket; per-shop
// DB-backed defaults are a v2 question.
//
// `setSort(next)` resets direction to that sort's natural default
// (e.g. switching to "next activity" snaps to ascending = soonest
// first). `toggleDir()` flips the current direction without changing
// the sort key.
export function useListSort(entity: ListSortEntity): {
  sort: ListSort
  dir: ListSortDir
  setSort: (next: ListSort) => void
  toggleDir: () => void
} {
  const [state, setState] = useState<StoredSort>(() => readInitial(entity))

  // Persist state to localStorage whenever it changes.
  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey(entity), JSON.stringify(state))
    } catch {
      // Non-fatal — in-memory state still reflects the change.
    }
  }, [entity, state])

  // Sync if the storage value changed in another tab.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const key = storageKey(entity)
    function onStorage(event: StorageEvent) {
      if (event.key !== key) return
      if (!event.newValue) return
      try {
        const parsed = JSON.parse(event.newValue) as Partial<StoredSort>
        if (
          parsed.sort &&
          (LIST_SORTS as readonly string[]).includes(parsed.sort) &&
          (parsed.dir === 'asc' || parsed.dir === 'desc')
        ) {
          setState({ sort: parsed.sort as ListSort, dir: parsed.dir })
        }
      } catch {
        // Ignore malformed values from another tab.
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [entity])

  const setSort = useCallback((next: ListSort) => {
    setState({ sort: next, dir: DEFAULT_DIR[next] })
  }, [])

  const toggleDir = useCallback(() => {
    setState((prev) => ({ ...prev, dir: prev.dir === 'asc' ? 'desc' : 'asc' }))
  }, [])

  return { sort: state.sort, dir: state.dir, setSort, toggleDir }
}
